import { Moment } from 'moment';
import { Manufacturer } from '../../operational/Manufacturers/Manufacturers.types';
import { SubArea } from '../../operational/SubAreas/SubAreas.types';
import { WorkOrderItemTag } from '../WorkOrderItemTags/WorkOrderItemTags.types';
import { WorkOrderComplementStatus } from '../WorkOrdersComplements/WorkOrdersComplements.types';

export enum WorkOrderItemType {
  Part = 'domain.enums.work_order_item_type.part',
  Service = 'domain.enums.work_order_item_type.service',
  Undefined = 'domain.enums.work_order_item_type.undefined',
  Kit = 'domain.enums.work_order_item_type.kit',
  Benefit = 'domain.enums.work_order_item_type.benefit',
  Bosch = 'domain.enums.work_order_item_type.bosch',
  Porto = 'domain.enums.work_order_item_type.porto',
}

export enum WorkOrderItemStatus {
  Red = 'domain.enums.work_order_item_status.red',
  Yellow = 'domain.enums.work_order_item_status.yellow',
  Orange = 'domain.enums.work_order_item_status.orange',
  Blue = 'domain.enums.work_order_item_status.blue',
  Green = 'domain.enums.work_order_item_status.green',
  Black = 'domain.enums.work_order_item_status.black',
}

export enum WorkOrderItemOrigin {
  Stock = 'domain.enums.item_origin.stock',
  Supplied = 'domain.enums.item_origin.supplied',
  Typed = 'domain.enums.item_origin.typed',
  Internal = 'domain.enums.item_origin.internal',
  Outsourced = 'domain.enums.item_origin.outsourced',
}

export interface WorkOrderItemStockStatus {
  situation: WorkOrderItemStatus;
  done: number;
  consolidation: number;
  reserved: number;
  remaining: number;
  quoted: number;
  purchased: number;
}

export interface WorkOrderItem {
  id: number;
  itemId: number;
  type: WorkOrderItemType;
  manufacturingCode: string;
  description: string;
  quantity: number;
  sku?: string;
  barcode?: number;
  companyCode?: number;
  internalCode?: number;
  unitPrice: number;
  unitCost: number;
  discount: number;
  isInternal: boolean;
  isInPecaAiBasket?: boolean;
  isInWomBasket?: boolean;
  pecaAiProductId?: string;
  womBasketProductId?: string;
  subArea: SubArea;
  subGroupId?: number;
  manufacturer: Manufacturer;
  supplier: {
    id: number;
    name: string;
  };
  isPaintedPart: boolean;
  tag: Omit<WorkOrderItemTag, 'description'> & { name: string };
  status: WorkOrderItemStockStatus;
  origin: WorkOrderItemOrigin;
  haveCommission: boolean;
  paint: number;
  change: number;
  deletedAt?: string;
  repair: number;
  kitId?: number;
  kitName?: string;
  employee?: {
    id: number;
    name: string;
    nickName: string;
  };
  isItemRejectedByCustomer?: boolean;
  itemRejectedReason?: string;
  benefitId?: number;
  benefitName?: string;
  benefitServiceCode?: string;
  benefitMaterialId?: number;
  isCourtesy?: boolean;
  benefitDateUsed?: boolean;
  groupingId?: string;
  groupingName?: string;
  portoBatteryLabelCode?: string | null;
  portoTaskId?: number;
}

export interface ListWorkOrderItemResult {
  nextToken: number;
  data: WorkOrderItem[];
  workOrderComplementId: number;
  totalDiscountPart: number;
  partTotal: number;
  totalDiscountService: number;
  serviceTotal: number;
  total: number;
}

export interface CreateWorkOrderItemRequest {
  complementId: number;
  itemType: WorkOrderItemType;
  itemId?: number;
  itemName?: string;
  subAreaId?: number;
  manufacturerId?: number;
  supplierId?: number;
  isInternal: boolean;
  quantity: number;
  unitPrice: number;
  unitCost: number;
  discount: number;
  isPaintedPart?: boolean;
  paint?: number;
  change?: number;
  repair?: number;
  employeeId?: number;
}

export interface CreateWorkOrderItemListRequest {
  complementId: number;
  workOrderItems: CreateWorkOrderItemRequest[];
}

export interface UpdateWorkOrderItemRequest {
  itemId: number;
  workOrderComplementId: number;
  itemName?: string;
  manufacturerId?: number;
  subAreaId?: number;
  supplierId: number;
  isInternal: boolean;
  quantity: number;
  unitPrice: number;
  discount: number;
  isPaintedPart: boolean;
  unitCost: number;
  tagId?: number;
  employeeId?: number;
}

export interface DeleteWorkOrderItemRequest {
  complementId: number;
  workOrderItemId: number;
  isItemRejectedByCustomer?: boolean;
  itemRejectedReason?: string;
  hasPecaAi?: boolean;
}

export interface ChangeWorkOrderItemDiscountRequest {
  complementId: number;
  priceDiscount: number;
  isProvided: boolean;
  type: WorkOrderItemType;
}

export const workorderTypeColor: Record<string, string> = {
  [WorkOrderItemType.Part]: 'blue',
  [WorkOrderItemType.Service]: 'yellow',
  [WorkOrderItemType.Kit]: 'violet',
  [WorkOrderItemType.Benefit]: 'violet',
  [WorkOrderItemType.Bosch]: 'red',
  [WorkOrderItemType.Porto]: 'indigo',
};

export const workorderTypeItem: Record<string, string> = {
  [`${WorkOrderItemType.Part}/${WorkOrderItemOrigin.Typed}`]: 'Peça: Digitada',
  [`${WorkOrderItemType.Part}/${WorkOrderItemOrigin.Stock}`]:
    'Peça: Cadastrada',
  [`${WorkOrderItemType.Part}/${WorkOrderItemOrigin.Supplied}`]:
    'Peça: Fornecida',
  [`${WorkOrderItemType.Part}/${WorkOrderItemOrigin.Internal}`]:
    'Peça: Interna',
  [`${WorkOrderItemType.Part}/${WorkOrderItemOrigin.Outsourced}`]:
    'Peça: Terceirizada',

  [`${WorkOrderItemType.Service}/${WorkOrderItemOrigin.Typed}`]:
    'Serviço: Digitado',
  [`${WorkOrderItemType.Service}/${WorkOrderItemOrigin.Stock}`]:
    'Serviço: Cadastrado',
  [`${WorkOrderItemType.Service}/${WorkOrderItemOrigin.Supplied}`]:
    'Serviço: Fornecido',
  [`${WorkOrderItemType.Service}/${WorkOrderItemOrigin.Internal}`]:
    'Serviço: Interno',
  [`${WorkOrderItemType.Service}/${WorkOrderItemOrigin.Outsourced}`]:
    'Serviço: Terceirizado',
};

export const workorderTypeItemSmall: Record<string, string> = {
  [WorkOrderItemType.Part]: 'Peça',
  [WorkOrderItemType.Service]: 'Serviço',
};

export interface ChangeWorkOrderItemTagRequest {
  complementId: number;
  itemId: number;
  tagId: number;
}

export interface ChangeWorkOrderItemRestoreRequest {
  complementId: number;
  id: number;
}

export interface WorkOrderItemHistoryFormValues {
  from: Moment;
  to: Moment;
  monthDate: string | null;
  companyId?: number;
  hasCustomer: boolean;
}

export interface WorkOrderItemHistoryRequest {
  partId: number;
  from: string;
  to: string;
  comapanyId?: number;
  customerId?: number;
  complementId: number;
}

export interface WorkOrderItemHistory {
  workOrderId: number | null;
  workOrderNumber: string;
  saleId: number | null;
  workOderDate: string;
  quantity: number;
  unitPrice: number;
  customer: string;
}

export interface WorkOrderPriceDiferenceListRequest {
  complementId?: number;
  companyId?: number;
}

export interface WorkOrderPriceDiference {
  purchaseCod?: string;
  description: string;
  quantity: number;
  priceCost: number;
  salePrice: number;
  priceDiference: number;
  refactoringMargin: number;
  minimumValue: number;
  partId: number;
  grossPrice?: number;
  grossPriceDiference?: number;
}

export interface WorkOrderItemRemoveFormValues {
  isItemRejectedByCustomer: boolean;
  itemRejectedReason: string;
}

export interface WorkOrderItemsSwapOperationalPart {
  complementId: number;
  workOrderItemId: number;
  newPartId: number;
  updatePrice: boolean;
}

export interface WorkOrderItemBenefitMaterial {
  complementItemId: number | undefined;
  id: number;
  name: string;
  unitCost: number;
  quantity: number;
  manufacturerId: number;
  isInternal: boolean;
  subGroupId?: number;
}

export interface WorkOrderItemBenefit {
  benefitId: number;
  complementItemId: number | undefined;
  name: string;
  unitCost: number;
  employeeId?: number;
  benefitServiceCode: string;
  isCourtesy: boolean;
  materials?: WorkOrderItemBenefitMaterial[];
}

export interface WorkOrderItemBenefitsCreateRequest {
  complementId: number;
  items: WorkOrderItemBenefit[];
}

export interface WorkOrderItemBenefitsRemoveRequest {
  complementId: number;
  itemId: number;
  items?: WorkOrderItem[];
}

export interface WorkOrderItemRefusePublicRequest {
  complementId: number;
  workOrderItemId: number;
  isItemRejectedByCustomer?: boolean;
  itemRejectedReason?: string;
}

export interface WorkOrderItemBatchRemoveRequest {
  complementId: number;
  items: WorkOrderItem[];
}

export enum WorkOrderItemActionType {
  Add = 'domain.enums.work_order_item_action_type.add',
  Remove = 'domain.enums.work_order_item_action_type.remove',
  Public_remove = 'domain.enums.work_order_item_action_type.public_remove',
  Edit = 'domain.enums.work_order_item_action_type.edit',
  Restore = 'domain.enums.work_order_item_action_type.restore',
  Change = 'domain.enums.work_order_item_action_type.change',
  Commission = 'domain.enums.work_order_item_action_type.commission',
}

export const WorkOrderItemActionTypeInfo: Record<
  WorkOrderItemActionType,
  { label: string; color: string }
> = {
  [WorkOrderItemActionType.Public_remove]: {
    color: 'red',
    label: 'Removeu (público)',
  },
  [WorkOrderItemActionType.Remove]: {
    color: 'red',
    label: 'Removeu (interno)',
  },
  [WorkOrderItemActionType.Add]: {
    color: 'green',
    label: 'Adicionou',
  },
  [WorkOrderItemActionType.Change]: {
    color: 'gray',
    label: 'Trocou a peça',
  },
  [WorkOrderItemActionType.Commission]: {
    color: 'violet',
    label: 'Comissionou',
  },
  [WorkOrderItemActionType.Edit]: {
    color: 'blue',
    label: 'Editou',
  },
  [WorkOrderItemActionType.Restore]: {
    color: 'yellow',
    label: 'Restaurou',
  },
};

export enum WorkOrderItemActionItemType {
  Undefined = 'domain.enums.work_order_item_type.undefined',
  Part = 'domain.enums.work_order_item_type.part',
  Service = 'domain.enums.work_order_item_type.service',
}

export const WorkOrderItemActionItemTypeInfo: Record<
  WorkOrderItemActionItemType,
  { label: string; color: string }
> = {
  [WorkOrderItemActionItemType.Part]: {
    color: 'blue',
    label: 'Peça',
  },
  [WorkOrderItemActionItemType.Service]: {
    color: 'yellow',
    label: 'Serviço',
  },
  [WorkOrderItemActionItemType.Undefined]: {
    color: 'default',
    label: 'Não definido',
  },
};

export interface WorkOrderItemAction {
  id: number;
  complementId: number;
  userId: number;
  userName: string;
  actionType: WorkOrderItemActionType;
  complementStatus?: WorkOrderComplementStatus;
  items: [
    {
      workOrderItemId: number;
      itemDescription: string;
      type?: WorkOrderItemActionItemType;
      old: true;
    },
  ];
  actionAt: string;
}

export interface WorkOrderItemActionListResponse {
  nextToken: number;
  data: WorkOrderItemAction[];
}

export interface WorkOrderItemActionListRequest {
  complementId?: number;
  userIds?: string;
  nextToken?: number;
  rows?: number;
  order?: 0 | 1;
}
